import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from '@/lib/cn'
import { ToastContext, type Toast, type ToastContextValue, type ToastTone } from './useToast'
import styles from './toast.module.css'

const ICONS: Record<ToastTone, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

/** Errors stay until dismissed; confirmations do not need to linger. */
const DURATIONS: Record<ToastTone, number> = {
  success: 4000,
  info: 4500,
  warning: 7000,
  error: 0,
}

let sequence = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((entry) => entry.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (input: Omit<Toast, 'id'>) => {
      sequence += 1
      const id = `toast-${sequence}`
      // Cap the stack so a burst of failures cannot bury the screen.
      setToasts((current) => [...current.slice(-4), { ...input, id }])

      const duration = DURATIONS[input.tone]
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        )
      }
    },
    [dismiss],
  )

  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach((timer) => clearTimeout(timer))
      pending.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      dismiss,
      success: (title, description) => toast({ tone: 'success', title, description }),
      error: (title, description) => toast({ tone: 'error', title, description }),
      info: (title, description) => toast({ tone: 'info', title, description }),
    }),
    [toast, dismiss],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className={styles.viewport}>
          {toasts.map((entry) => {
            const Icon = ICONS[entry.tone]
            return (
              <div
                key={entry.id}
                className={cn(styles.toast, styles[entry.tone])}
                // Failures interrupt; confirmations wait their turn.
                role={entry.tone === 'error' ? 'alert' : 'status'}
                aria-live={entry.tone === 'error' ? 'assertive' : 'polite'}
              >
                <Icon className={styles.icon} aria-hidden="true" />
                <div className={styles.body}>
                  <p className={styles.title}>{entry.title}</p>
                  {entry.description ? <p className={styles.description}>{entry.description}</p> : null}
                </div>
                <button
                  type="button"
                  className={styles.close}
                  onClick={() => dismiss(entry.id)}
                  aria-label="Dismiss notification"
                >
                  <X aria-hidden="true" />
                </button>
              </div>
            )
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
